import { AgGridAngular } from '@ag-grid-community/angular';
import { FirstDataRenderedEvent, GridApi, GridReadyEvent } from '@ag-grid-community/core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { uniq } from 'lodash';
import { firstValueFrom } from 'rxjs';

import { injectManageBalanceSheetsAccountsModal } from '@widgets/new-manage-balance-sheets-accounts-modal/new-manage-balance-sheets-accounts-component-modal.component';

import { ExportExcelButtonComponent } from '@features/export-excel-button/export-excel-button.component';

import { injectGetAllVendorBalanceSheetsQuery } from '@models/balance-sheet-accounts/get-all-vendor-balance-sheets.query';
import { injectListIdOrganizationsQuery } from '@models/organization/list-id-organizations-query';
import { injectPivotSettings } from '@models/trial-attributes/trial-attributes';

import { ButtonComponent } from '@shared/components/button/button.component';
import { TablePanelComponent } from '@shared/components/table-panel/table-panel.components';
import { TableSkeletonComponent } from '@shared/components/table-skeleton/table-skeleton.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { injectFlags } from '@shared/services/launch-darkly.service';
import { injectPermissions } from '@shared/store/auth/auth.service';
import { MainQuery } from '@shared/store/main/main.query';
import { syncGridOptions } from '@shared/utils/ag-sync-grid-options.util';

import { buildBalanceSheetAccountRows } from './grid-config/balance-sheet-account-row-data.util';
import { balanceSheetAccountsGridConfig } from './grid-config/balance-sheet-accounts-grid.config';
import { BalanceSheetAccountView } from './grid-config/grid-config-context';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AgGridAngular,
    TableSkeletonComponent,
    FormsModule,
    TablePanelComponent,
    ExportExcelButtonComponent,
    ButtonComponent,
    TooltipDirective,
  ],
  selector: 'aux-balance-sheet-accounts',
  templateUrl: './balance-sheet-accounts.component.html',
})
export class BalanceSheetAccountsComponent {
  mainQuery = inject(MainQuery);
  router = inject(Router);

  listIdOrganizationsQuery = injectListIdOrganizationsQuery();

  allVendorIds = computed(() => {
    const orgs = this.listIdOrganizationsQuery.data();

    if (!orgs) return [];

    return orgs
      .filter((org) => org.organization_type === 'ORGANIZATION_VENDOR')
      .map((org) => org.id);
  });

  allBalanceSheetsQuery = injectGetAllVendorBalanceSheetsQuery({
    vendorIdsSignal: this.allVendorIds,
  });
  flags = injectFlags({
    journalEntryApManualClearing: 'journal_entry_ap_manual_clearing',
  });
  manageBalanceSheetsAccountsModal = injectManageBalanceSheetsAccountsModal();
  permissions = injectPermissions({
    manageBalanceSheetsAccounts: 'PERMISSION_UPDATE_BALANCE_SHEET_ACCOUNT',
  });
  pivotSettings = injectPivotSettings();

  gridAPI = signal<GridApi<BalanceSheetAccountView> | null>(null);

  rowData = computed(() => {
    const flags = this.flags();
    const rawData = this.allBalanceSheetsQuery.data() ?? [];

    if (rawData.length === 0) return [];

    return buildBalanceSheetAccountRows(rawData, flags.journalEntryApManualClearing);
  });
  vendorIdToNameMap = computed(() => {
    const orgs = this.listIdOrganizationsQuery.data();
    const map = new Map<string, string>();

    if (!orgs) return map;

    orgs
      .filter((org) => org.organization_type === 'ORGANIZATION_VENDOR')
      .forEach((org) => {
        if (org.id && org.name) {
          map.set(org.id, org.name);
        }
      });

    return map;
  });
  gridOptions = computed(() => {
    const pivotSettings = this.pivotSettings.data();
    const currentGridData = this.rowData();
    const currentVendorIdToNameMap = this.vendorIdToNameMap();

    return untracked(() => {
      return balanceSheetAccountsGridConfig({
        gridData: currentGridData,
        pivotSettings,
        vendorIdToNameMap: currentVendorIdToNameMap,
      });
    });
  });
  loading = computed(
    () =>
      this.listIdOrganizationsQuery.isLoading() ||
      this.allBalanceSheetsQuery.isLoading() ||
      this.pivotSettings.loading()
  );
  manageBalanceSheetsAccountsPermissionTooltip = computed(() => {
    const permission = this.permissions().manageBalanceSheetsAccounts;

    if (!permission) {
      return MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION;
    }

    return '';
  });

  _gridOptionsSync = effect(() => {
    const gridAPI = untracked(() => this.gridAPI()!);
    const options = this.gridOptions();

    if (!gridAPI || !options) return;
    syncGridOptions(gridAPI, options);
  });

  exportData = () => {
    const api = this.gridAPI()!;

    const trialName = this.mainQuery.selectedTrial()?.name || '';

    api.exportDataAsExcel({
      fileName: `${trialName}_Balance_Sheet_Accounts.xlsx`,
    });
  };

  onFirstDataRendered = (params: FirstDataRenderedEvent<BalanceSheetAccountView>) => {
    const api = params.api;

    api.sizeColumnsToFit();
  };

  onGridReady = (params: GridReadyEvent<BalanceSheetAccountView>) => {
    this.gridAPI.set(params.api);
  };

  async openManageBalanceSheetsAccountsModal(): Promise<void> {
    const rowData = this.rowData();
    const programOptions = uniq(rowData.map((row) => row.program).filter(Boolean));
    const projectOptions = uniq(rowData.map((row) => row.project).filter(Boolean));

    const modalRef = this.manageBalanceSheetsAccountsModal({
      programOptions,
      projectOptions,
    });

    const { data } = await firstValueFrom(modalRef.afterClosed$);

    if (data) {
      this.pivotSettings.refreshAttributes();
      void this.allBalanceSheetsQuery.refetch();
    }
  }

  viewJournalEntries = () => {
    const route = `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.JOURNAL_ENTRIES}`;

    void this.router.navigateByUrl(route);
  };
}
